import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, HardDrive, Bell, Shield, Users, Trash, User, Camera, Edit } from 'lucide-react';
import { useFileStore } from '../stores/file-store';
import { useUserStore } from '../stores/user-store';
import { formatFileSize } from '../utils/file-types';
import { cn } from '../utils/cn';
import { useI18n } from '../i18n';
import { PERMISSION_LABELS } from '../utils/permissions';
import StorageCleanup from '../components/settings/StorageCleanup';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { files } = useFileStore();
  const { t } = useI18n();
  const { profile } = useUserStore();
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    phone_number: '',
    language_preference: ''
  });
  
  const [isUploading, setIsUploading] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [showStorageCleanup, setShowStorageCleanup] = React.useState(false);
  
  React.useEffect(() => {
    useUserStore.getState().fetchProfile();
  }, []);

  React.useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        phone_number: profile.phone_number || '',
        language_preference: profile.language_preference || 'zh'
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaveSuccess(false);
      await useUserStore.getState().updateProfile(formData);
      setIsEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      await useUserStore.getState().updateAvatar(file);
    } catch (error) {
      console.error('Error uploading avatar:', error);
    } finally {
      setIsUploading(false);
    }
  };
  
  // Calculate storage usage
  const storageByType = files.reduce((acc, file) => {
    acc[file.type] = (acc[file.type] || 0) + file.size;
    return acc;
  }, {} as Record<string, number>);

  const totalStorage = Object.values(storageByType).reduce((a, b) => a + b, 0);
  const storageLimit = 10 * 1024 * 1024 * 1024; // 10GB
  const storagePercentage = Math.min((totalStorage / storageLimit) * 100, 100);
  const usedStorageInMB = totalStorage / (1024 * 1024);
  const totalStorageInGB = storageLimit / (1024 * 1024 * 1024);

  const settingsMenu = [
    { 
      id: 'profile',
      label: t('settingsMenu.profile'),
      icon: User,
      description: t('profileSettings')
    },
    { 
      id: 'storage',
      label: t('settingsMenu.storage'),
      icon: HardDrive,
      description: t('storageManagementTitle')
    },
    { 
      id: 'notifications',
      label: t('settingsMenu.notifications'),
      icon: Bell,
      description: t('notificationSettingsDesc')
    },
    { 
      id: 'security',
      label: t('settingsMenu.security'),
      icon: Shield,
      description: t('securitySettingsDesc')
    },
    { 
      id: 'sharing',
      label: t('settingsMenu.sharing'),
      icon: Users,
      description: t('sharingCollabDesc')
    },
    { 
      id: 'trash',
      label: t('settingsMenu.trash'),
      icon: Trash,
      description: t('trashManagementDesc')
    }
  ];

  const activeSetting = location.pathname.split('/')[3] || 'storage';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 mr-3 bg-gray-100 text-gray-600 rounded-md">
            <Settings size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">{t('settings')}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Settings Menu */}
        <div className="space-y-1">
          {settingsMenu.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(`/uadmin/settings/${item.id}`)}
              className={cn(
                "w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                activeSetting === item.id
                  ? "bg-teal-100 text-teal-900"
                  : "text-gray-700 hover:bg-gray-100"
              )}
            >
              <item.icon size={18} className="mr-2" />
              {item.label}
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="md:col-span-3 space-y-6">
          {activeSetting === 'profile' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                     <h2 className="text-lg font-medium text-gray-800">{t('profileSettings')}</h2>
                    {saveSuccess && (
                       <div className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-md">
                         {t('savedSuccess')}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-start space-x-6 mb-6">
                    <div className="relative group">
                      <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gray-100">
                        <img
                          src={profile?.avatar_url || `https://ui-avatars.com/api/?name=${profile?.name || 'User'}&background=0D9488&color=fff`}
                          alt={profile?.name || 'User profile'}
                          className="w-full h-full object-cover"
                        />
                        
                        {isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                      
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity rounded-full">
                        <Camera size={20} />
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          disabled={isUploading}
                        />
                      </label>
                    </div>
                    
                    <div className="ml-4 flex-1">
                       <h3 className="text-lg font-medium text-gray-900">{profile?.name || t('notSet')}</h3>
                      <p className="text-sm text-gray-500">{profile?.email}</p>
                    </div>
                    
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
                      >
                         {t('editProfile')}
                      </button>
                    )}
                  </div>
                  
                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                         <label className="block text-sm font-medium text-gray-700">{t('nameLabel')}</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                      
                      <div>
                         <label className="block text-sm font-medium text-gray-700">{t('phoneLabel')}</label>
                        <input
                          type="tel"
                          value={formData.phone_number}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                      
                      <div>
                         <label className="block text-sm font-medium text-gray-700">{t('languagePreference')}</label>
                        <select
                          value={formData.language_preference}
                          onChange={(e) => setFormData(prev => ({ ...prev, language_preference: e.target.value }))}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
                        >
                          <option value="zh">{t('chinese')}</option>
                          <option value="en">{t('english')}</option>
                        </select>
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditing(false);
                            setFormData({
                              name: profile?.name || '',
                              phone_number: profile?.phone_number || '',
                              language_preference: profile?.language_preference || 'zh'
                            });
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
                        >
                          {t('save')}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                           <div className="text-sm font-medium text-gray-700">{t('nameLabel')}</div>
                           <div className="mt-1 text-sm text-gray-900">{profile?.name || t('notSet')}</div>
                        </div>

                        <div>
                           <div className="text-sm font-medium text-gray-700">{t('phoneLabel')}</div>
                           <div className="mt-1 text-sm text-gray-900">{profile?.phone_number || t('notSet')}</div>
                        </div>

                        <div>
                           <div className="text-sm font-medium text-gray-700">{t('languagePreference')}</div>
                          <div className="mt-1 text-sm text-gray-900">
                             {profile?.language_preference === 'zh' ? t('chinese') : t('english')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-6">
                   <h3 className="text-lg font-medium text-gray-800 mb-4">{t('accountInfo')}</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                         <h4 className="text-sm font-medium text-gray-900">{t('accountRole')}</h4>
                        <p className="text-sm text-gray-500">
                           {profile?.role ? PERMISSION_LABELS[profile.role] : t('userRole')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                         <h4 className="text-sm font-medium text-gray-900">{t('registeredAt')}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(profile?.created_at || '').toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-3">
                      <div>
                         <h4 className="text-sm font-medium text-gray-900">{t('lastPasswordChange')}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(profile?.last_password_change || '').toLocaleDateString()}
                        </p>
                      </div>
                       <button className="text-sm text-teal-600 hover:text-teal-700">
                         {t('changePassword')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSetting === 'storage' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-6">
                   <h2 className="text-lg font-medium text-gray-800 mb-4">{t('storageOverviewTitle')}</h2>
                  
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-1">
                       <span className="text-sm text-gray-600">{t('used')} {usedStorageInMB < 0.1 ? '< 0.1' : usedStorageInMB.toFixed(1)} MB</span>
                       <span className="text-sm text-gray-600">{t('total')} {totalStorageInGB} GB</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full">
                      <div 
                        className={cn(
                          "h-2 rounded-full transition-all",
                          storagePercentage > 90 ? 'bg-red-500' : 
                          storagePercentage > 75 ? 'bg-yellow-500' : 
                          'bg-teal-500'
                        )} 
                        style={{ width: `${storagePercentage}%` }}
                      >
                        {storagePercentage > 20 && (
                          <div className="relative w-full h-full">
                            <span className="absolute -top-6 right-0 text-xs font-medium">
                              {Math.round(storagePercentage)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {storagePercentage > 75 && (
                      <p className={cn(
                        "mt-2 text-xs",
                        storagePercentage > 90 ? 'text-red-600' : 'text-yellow-600'
                      )}>
                        {storagePercentage > 90 
                          ? t('storageAlmostFullMsg')
                          : t('storageGettingFullMsg')}
                      </p>
                    )}
                  </div>

                  <div className="space-y-4">
                     <h3 className="text-sm font-medium text-gray-700">{t('storageByTypeTitle')}</h3>
                    <div className="space-y-2">
                      {Object.entries(storageByType).map(([type, size]) => (
                        <div key={type} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md">
                          <div className="flex items-center">
                            <div className={cn(
                              "p-1.5 mr-3 rounded-md",
                              type === 'document' ? 'bg-blue-50 text-blue-500' :
                              type === 'image' ? 'bg-emerald-50 text-emerald-500' :
                              type === 'video' ? 'bg-red-50 text-red-500' :
                              'bg-gray-50 text-gray-500'
                            )}>
                              <HardDrive size={16} />
                            </div>
                            <span className="text-sm font-medium text-gray-700 capitalize">{type}</span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatFileSize(size)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-6">
                   <h2 className="text-lg font-medium text-gray-800 mb-4">{t('storageManagementTitle')}</h2>
                  <div className="space-y-4">
                    <button 
                      onClick={() => setShowStorageCleanup(true)}
                      className="w-full flex items-center justify-between p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div>
                         <h3 className="text-sm font-medium text-gray-800">{t('cleanUpAssistantTitle')}</h3>
                         <p className="text-xs text-gray-500">{t('cleanUpAssistantDesc')}</p>
                      </div>
                       <span className="text-teal-600">{t('run')}</span>
                    </button>

                    <button className="w-full flex items-center justify-between p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div>
                         <h3 className="text-sm font-medium text-gray-800">{t('storageAnalyticsTitle')}</h3>
                         <p className="text-xs text-gray-500">{t('storageAnalyticsDesc')}</p>
                      </div>
                       <span className="text-teal-600">{t('view')}</span>
                    </button>

                    <button className="w-full flex items-center justify-between p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div>
                         <h3 className="text-sm font-medium text-gray-800">{t('upgradeStorageTitle')}</h3>
                         <p className="text-xs text-gray-500">{t('upgradeStorageDesc')}</p>
                      </div>
                       <span className="text-teal-600">{t('upgrade')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSetting === 'notifications' && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">{t('notificationSettingsTitle')}</h2>
              <p className="text-gray-500">{t('notificationSettingsDesc')}</p>
            </div>
          )}

          {activeSetting === 'security' && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">{t('securitySettingsTitle')}</h2>
              <p className="text-gray-500">{t('securitySettingsDesc')}</p>
            </div>
          )}

          {activeSetting === 'sharing' && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">{t('sharingCollabTitle')}</h2>
              <p className="text-gray-500">{t('sharingCollabDesc')}</p>
            </div>
          )}

          {activeSetting === 'trash' && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">{t('trashManagementTitle')}</h2>
              <p className="text-gray-500">{t('trashManagementDesc')}</p>
            </div>
          )}
        </div>
      </div>

      {/* 存储清理面板 */}
      <StorageCleanup
        isOpen={showStorageCleanup}
        onClose={() => setShowStorageCleanup(false)}
      />
    </div>
  );
};

export default SettingsPage;